<template>
  <div v-if="showActionButtons" class="hidden tablet-h:flex items-center">
    <UiButton icon="Move" class="mr-3" @click="handleMoveClick">
      Move
    </UiButton>
    <UiButton
      v-if="isSingleFileSelected"
      class="mr-3 hidden min-[1240px]:flex"
      icon="Archive"
      :disabled="areSelectedFilesInactive || isCoOwner || isAuthor"
      @click="handleArchiveClick"
    >
      Archive
    </UiButton>
    <UiButton
      v-if="areAllFolders"
      class="mr-3"
      icon="Delete"
      @click="handleDeleteClick"
    >
      Delete
    </UiButton>
  </div>
</template>

<script setup lang="ts">
import { UiButton } from '@dacr/ui'
import {
  CopyrightStatus,
  FileRole,
  type Item,
} from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'
import { computed } from 'vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { Table } from '@dacr/ui'
import { TableTypes } from '@/shared/types'

const props = defineProps<{
  selectedItems: Item[]
}>()

const { open } = useDialog()
const table = Table.useTable(TableTypes.Explorer)

const showActionButtons = computed(() => props.selectedItems.length > 0)

const areAllFiles = computed(
  () => props.selectedItems.length > 0 && props.selectedItems.every(isFile)
)

const areAllFolders = computed(
  () => props.selectedItems.length > 0 && props.selectedItems.every(isFolder)
)

const isSingleFileSelected = computed(
  () => areAllFiles.value && props.selectedItems.length === 1
)

const areSelectedFilesInactive = computed(() =>
  props.selectedItems
    .filter(isFile)
    .some((file) => file.copyright?.status === CopyrightStatus.Inactive)
)

const isCoOwner = computed(() => {
  const first = props.selectedItems[0]
  return !!(first && isFile(first) && first.role === FileRole.CoOwner)
})

const isAuthor = computed(() => {
  const first = props.selectedItems[0]
  return !!(first && isFile(first) && first.role === FileRole.Author)
})

const handleMoveClick = () => {
  open(Dialogs.MoveItems, {
    props: { ids: props.selectedItems.map((item) => item.id) },
    events: {
      close() {
        table.resetSelection()
      },
    },
  })
}

const handleArchiveClick = () => {
  open(Dialogs.ArchiveFiles, {
    props: { files: props.selectedItems.filter(isFile) },
  })
}

const handleDeleteClick = () => {
  open(Dialogs.DeleteFolders, {
    props: { items: props.selectedItems },
  })
}
</script>
